import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const NAV = [
  { name: "Index", to: "/", section: "hero" },
  { name: "Work", to: "/", section: "work" },
  { name: "Practice", to: "/", section: "practice" },
  { name: "Stack", to: "/", section: "stack" },
  { name: "Contact", to: "/", section: "contact" },
];

const SOCIAL = [
  { label: "GitHub", short: "GH", url: "https://github.com/Teemi2106" },
  {
    label: "LinkedIn",
    short: "LI",
    url: "https://www.linkedin.com/in/timi-gbenga-ba782b304",
  },
  { label: "Twitter", short: "TW", url: "https://x.com/timmy_btw" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("hero");
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);

      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      setProgress(max > 0 ? Math.min(1, y / max) : 0);

      // Active section: the last one whose top has crossed 45% of the viewport
      const line = window.innerHeight * 0.45;
      let current = "hero";
      NAV.forEach(({ section }) => {
        const el = document.getElementById(section);
        if (el && el.getBoundingClientRect().top <= line) current = section;
      });
      setActive(current);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  // Lock body scroll behind the mobile sheet
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = useCallback(
    (section) => {
      setOpen(false);
      const scroll = () => {
        const el = document.getElementById(section);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };

      if (onHome) {
        scroll();
      } else {
        navigate("/");
        // Wait for the home route to paint before seeking the anchor
        requestAnimationFrame(() => setTimeout(scroll, 120));
      }
    },
    [onHome, navigate]
  );

  const goResume = () => {
    setOpen(false);
    navigate("/resume");
  };

  return (
    <>
      {/* Scroll progress - a hairline drawn across the top edge */}
      <div
        className="fixed top-0 left-0 right-0 h-px z-[80] origin-left bg-ink/80"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />

      <header className="fixed top-0 left-0 right-0 z-[70] pt-4 md:pt-5 pointer-events-none">
        <div className="mx-auto w-full max-w-shell px-4 md:px-10">
          <div
            className={[
              "pointer-events-auto flex items-center justify-between",
              "rounded-full transition-all duration-700 ease-glass",
              scrolled
                ? "glass-bar px-3 md:px-4 py-2 md:py-2.5"
                : "px-3 md:px-4 py-2 md:py-2.5 bg-transparent border border-transparent",
            ].join(" ")}
          >
            {/* Wordmark */}
            <button
              onClick={() => go("hero")}
              className="group flex items-center gap-3 pl-1 pr-2"
              aria-label="Timi Gbenga, back to top"
            >
              <span className="relative grid h-8 w-8 place-items-center rounded-full border border-rule-2">
                <span className="mono text-[10px] font-medium tracking-[0.05em] text-ink">
                  TG
                </span>
              </span>
              <span className="hidden sm:block text-[13px] font-medium tracking-[-0.01em] text-ink">
                Timi Gbenga
              </span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((item) => {
                const isActive = onHome && active === item.section;
                return (
                  <button
                    key={item.name}
                    onClick={() => go(item.section)}
                    className={[
                      "relative px-3.5 py-2 rounded-full text-[13px] tracking-[-0.005em]",
                      "transition-colors duration-500 ease-glass",
                      isActive
                        ? "text-ink"
                        : "text-ink-45 hover:text-ink-70",
                    ].join(" ")}
                  >
                    {isActive && (
                      <span className="absolute inset-0 rounded-full bg-ink/[0.055]" />
                    )}
                    <span className="relative">{item.name}</span>
                  </button>
                );
              })}
            </nav>

            {/* Right cluster */}
            <div className="flex items-center gap-1.5">
              <div className="hidden lg:flex items-center gap-0.5 mr-1">
                {SOCIAL.map((s) => (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="mono px-2 py-1.5 text-[10px] tracking-[0.1em] text-ink-45 hover:text-ink transition-colors duration-400"
                  >
                    {s.short}
                  </a>
                ))}
              </div>

              <button
                onClick={goResume}
                aria-current={location.pathname === "/resume" ? "page" : undefined}
                className={[
                  "hidden sm:inline-flex items-center rounded-full px-4 py-2",
                  "text-[12.5px] font-medium tracking-[-0.005em]",
                  "transition-opacity duration-500 hover:opacity-85",
                  location.pathname === "/resume"
                    ? "border border-ink text-ink"
                    : "bg-ink text-ground-raise",
                ].join(" ")}
              >
                Resume
              </button>

              {/* Mobile trigger: two rules that fold into an X */}
              <button
                onClick={() => setOpen((v) => !v)}
                className="lg:hidden grid h-9 w-9 place-items-center rounded-full"
                aria-label={open ? "Close menu" : "Open menu"}
                aria-expanded={open}
              >
                <span className="relative block h-3 w-4">
                  <span
                    className={`absolute left-0 block h-px w-4 bg-ink transition-transform duration-500 ease-glass ${
                      open ? "top-1.5 rotate-45" : "top-0.5"
                    }`}
                  />
                  <span
                    className={`absolute left-0 block h-px w-4 bg-ink transition-transform duration-500 ease-glass ${
                      open ? "top-1.5 -rotate-45" : "top-2.5"
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sheet */}
      <div
        className={[
          "fixed inset-0 z-[65] lg:hidden transition-opacity duration-500 ease-glass",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
      >
        <div
          className="absolute inset-0 bg-ground/70 backdrop-blur-2xl"
          onClick={() => setOpen(false)}
        />

        <div className="relative flex h-full flex-col justify-between px-6 pt-28 pb-10">
          <nav>
            {NAV.map((item, i) => (
              <button
                key={item.name}
                onClick={() => go(item.section)}
                className="group flex w-full items-baseline border-b border-rule py-5 text-left"
                style={{
                  transitionDelay: `${open ? 90 + i * 55 : 0}ms`,
                  opacity: open ? 1 : 0,
                  transform: open ? "none" : "translateY(12px)",
                  transition:
                    "opacity .7s var(--ease-glass), transform .7s var(--ease-glass)",
                }}
              >
                <span className="display-sm text-ink">{item.name}</span>
              </button>
            ))}
          </nav>

          <div className="space-y-6">
            <button
              onClick={goResume}
              className="w-full rounded-full bg-ink py-4 text-[14px] font-medium text-ground-raise"
            >
              View Resume
            </button>
            <div className="flex items-center gap-6">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="label link-draw text-ink-70"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
